/**
 * Visual Dashboard — consolidated charts + KPIs across all tabs.
 */
import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { LayoutDashboard, TrendingUp, Wallet, ShieldAlert, Users, Building2, HardHat } from 'lucide-react';
import {
  PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
  RadialBarChart, RadialBar, ComposedChart, Line,
} from 'recharts';
import PrintButton from './PrintButton';
import BudgetNarrative from './BudgetNarrative';
import SCurves from './SCurves';

const COLORS = ['#ef4444', '#f97316', '#eab308', '#22c55e', '#06b6d4', '#3b82f6', '#a855f7', '#ec4899', '#14b8a6', '#f59e0b'];
import { makeFmt } from './currency';

export default function Dashboard({ calculation, riskEval }) {
  const fmt$ = useMemo(() => makeFmt(calculation), [calculation]);
  const phasePie = useMemo(() => (calculation?.phases || []).map((p, i) => ({
    name: p.phase_label, value: p.cost_usd, fill: COLORS[i % COLORS.length],
  })), [calculation]);

  const phaseLabourBars = useMemo(() => (calculation?.phases || []).map(p => ({
    phase: p.phase_label.split(' ')[0],
    Labour: p.labour_cost_usd, Materials: p.material_cost_usd, Hours: p.labour_hours,
  })), [calculation]);

  const tradeData = useMemo(() => {
    const arr = (calculation?.labour_summary?.by_trade || []).slice(0, 8);
    return arr.map(t => ({ name: t.description, hours: t.hours }));
  }, [calculation]);

  const breakdown = useMemo(() => {
    if (!calculation) return [];
    const c = calculation.contract || {};
    return [
      { name: 'Direct cost',  value: c.base_cost_usd },
      { name: 'Inflation',    value: calculation.inflation_uplift_usd || 0 },
      { name: 'Contingency',  value: c.criticality_contingency_usd },
      { name: 'Overhead',     value: c.overhead_usd },
      { name: 'Risk',         value: c.risk_usd },
      { name: 'Margin',       value: c.margin_usd },
    ].filter(x => (x.value || 0) > 0);
  }, [calculation]);

  if (!calculation) {
    return <Card><CardContent className="p-8 text-center text-sm text-slate-500">Run a calculation first to see the consolidated dashboard.</CardContent></Card>;
  }

  return (
    <div className="space-y-4 print-section" data-testid="dashboard-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-base font-semibold text-slate-900 inline-flex items-center gap-1.5">
          <LayoutDashboard className="h-4 w-4" />Project Dashboard
        </h3>
        <PrintButton testid="dashboard-print-btn" />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
        <Kpi accent="bg-emerald-700" icon={<TrendingUp className="h-4 w-4" />} label="Final cost" value={fmt$(calculation.final_cost_usd)} />
        <Kpi accent="bg-orange-600"  icon={<Building2 className="h-4 w-4" />}  label="Direct cost" value={fmt$(calculation.total_direct_cost_usd)} />
        <Kpi accent="bg-blue-600"    icon={<HardHat className="h-4 w-4" />}    label="Labour hours" value={Number(calculation.total_labour_hours||0).toLocaleString()} />
        <Kpi accent="bg-indigo-600"  icon={<Users className="h-4 w-4" />}      label="Avg workforce" value={`${calculation.indicative_avg_workforce} pax`} />
        <Kpi accent="bg-rose-600"    icon={<ShieldAlert className="h-4 w-4" />} label="Expected risk" value={fmt$(riskEval?.expected_risk_cost_usd)} />
        <Kpi accent="bg-amber-600"   icon={<Wallet className="h-4 w-4" />}     label="Inflation uplift" value={`${calculation.inflation_uplift_pct || 0}%`} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card><CardHeader className="pb-2"><CardTitle className="text-base">Cost build-up</CardTitle></CardHeader><CardContent>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={breakdown} layout="vertical" margin={{ left: 60 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" fontSize={11} tickFormatter={v => fmt$(v, { compact: true })} />
              <YAxis type="category" dataKey="name" fontSize={11} width={120} />
              <Tooltip formatter={v => fmt$(v)} />
              <Bar dataKey="value" fill="#10b981" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent></Card>

        <Card><CardHeader className="pb-2"><CardTitle className="text-base">Cost split by phase</CardTitle></CardHeader><CardContent>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={phasePie} dataKey="value" nameKey="name" outerRadius={90} label={(e) => `${(e.percent*100).toFixed(0)}%`}>
                {phasePie.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip formatter={v => fmt$(v)} />
            </PieChart>
          </ResponsiveContainer>
        </CardContent></Card>

        <Card><CardHeader className="pb-2"><CardTitle className="text-base">Labour vs materials per phase</CardTitle></CardHeader><CardContent>
          <ResponsiveContainer width="100%" height={260}>
            <ComposedChart data={phaseLabourBars}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="phase" fontSize={11} />
              <YAxis fontSize={11} />
              <Tooltip formatter={v => fmt$(v)} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="Labour"    stackId="a" fill="#6366f1" />
              <Bar dataKey="Materials" stackId="a" fill="#f97316" />
              <Line type="monotone" dataKey="Hours" stroke="#0ea5e9" strokeWidth={2} dot={false} yAxisId={0} />
            </ComposedChart>
          </ResponsiveContainer>
        </CardContent></Card>

        <Card><CardHeader className="pb-2"><CardTitle className="text-base">Top trades by hours</CardTitle></CardHeader><CardContent>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={tradeData} layout="vertical" margin={{ left: 60 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" fontSize={10} />
              <YAxis type="category" dataKey="name" fontSize={10} width={140} />
              <Tooltip formatter={v => Number(v).toLocaleString() + ' hrs'} />
              <Bar dataKey="hours" fill="#0ea5e9" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent></Card>
      </div>

      {/* Project context strip */}
      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base">Project context</CardTitle></CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2 text-xs">
            <Tag k="Sector"     v={calculation.sector} />
            <Tag k="Sub-sector" v={calculation.sub_sector} />
            <Tag k="Region"     v={calculation.region} />
            {calculation.city && <Tag k="City" v={calculation.city} />}
            <Tag k="Contract"   v={calculation.contract?.contract_label} />
            <Tag k="Criticality" v={calculation.criticality} />
            <Tag k="Duration"   v={`${calculation.project_duration_months} mo`} />
            {calculation.gross_floor_area_m2 ? <Tag k="GFA" v={`${calculation.gross_floor_area_m2.toLocaleString()} m²`} /> : null}
            {calculation.bim_lod ? <Tag k="BIM LOD"   v={calculation.bim_lod} /> : null}
            {calculation.shift_pattern ? <Tag k="Shift" v={calculation.shift_pattern} /> : null}
            {calculation.procurement_model ? <Tag k="Procurement" v={calculation.procurement_model} /> : null}
            {calculation.site_complexity ? <Tag k="Complexity" v={calculation.site_complexity} /> : null}
            {Number(calculation.annual_inflation_pct) > 0 ? <Tag k="Inflation" v={`${calculation.annual_inflation_pct}% / yr`} /> : null}
          </div>
        </CardContent>
      </Card>

      <BudgetNarrative calculation={calculation} />

      {/* Wave 9: Discipline-wise costs and S-curves */}
      <SCurves
        scurve={calculation.scurve}
        phases={calculation.phases}
        subDisciplineSummary={calculation.sub_discipline_summary}
        calculation={calculation}
      />
    </div>
  );
}

function Kpi({ accent, icon, label, value }) {
  return (
    <div className="border border-slate-200 rounded-xl p-3 flex items-center gap-3 stx-kpi-tile bg-gradient-to-br from-white to-slate-50">
      <div className={`p-2.5 rounded-lg text-white shadow-sm ${accent}`}>{icon}</div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 truncate">{label}</div>
        <div className="font-black text-slate-900 truncate text-base font-mono" style={{ fontFeatureSettings: '"tnum"' }}>{value}</div>
      </div>
    </div>
  );
}
function Tag({ k, v }) {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-slate-100 border border-slate-200">
      <span className="text-slate-500">{k}:</span>
      <span className="font-medium text-slate-800 capitalize">{v}</span>
    </span>
  );
}
